import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Key, Copy, Trash2, RefreshCw, Loader2, Shield, Check,
  Store, Utensils, Wrench, Download, Clock, Activity, Image,
  MapPin, DollarSign, Bell, Mail, MessageSquare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScraperDashboard() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [scrapingParts, setScrapingParts] = useState(false);
  const [improvingPhotos, setImprovingPhotos] = useState(false);
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyType, setNewKeyType] = useState('restaurant');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState({
    restaurants: 0,
    menu_items: 0,
    auto_parts_stores: 0,
    auto_parts_ads: 0,
    recent_scrapes: []
  });
  
  // Notification logs
  const [notificationLogs, setNotificationLogs] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const [keysRes, statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/api-keys/list`, { headers, withCredentials: true }),
        axios.get(`${API}/scraper/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/notifications/logs?limit=20`, { headers, withCredentials: true })
      ]);
      
      setApiKeys(keysRes.data.api_keys || []);
      setStats(statsRes.data || {});
      setNotificationLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }
    
    setGeneratingKey(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await axios.post(
        `${API}/api-keys/generate`,
        {
          name: newKeyName,
          type: newKeyType,
          permissions: ['scrape_menu', 'scrape_parts', 'read_data']
        },
        { headers, withCredentials: true }
      );
      
      setGeneratedKey(response.data);
      toast.success('API key generated!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key?')) return;
    
    try {
      const headers = { ...getAuthHeaders() };
      await axios.delete(`${API}/api-keys/${keyId}`, { headers, withCredentials: true });
      toast.success('API key revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleScrapeAllRestaurants = async () => {
    setScraping(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await axios.post(
        `${API}/scraper/restaurants/scrape-all`,
        {},
        { headers, withCredentials: true }
      );
      
      toast.success(`Scraped ${response.data.restaurants_added} restaurants with ${response.data.menu_items_added} menu items!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scrape restaurants');
    } finally {
      setScraping(false);
    }
  };

  const handleScrapeAllAutoParts = async () => {
    setScrapingParts(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await axios.post(
        `${API}/scraper/auto-parts/scrape-all`,
        { count: 1000 },
        { headers, withCredentials: true }
      );
      
      toast.success(`Scraped ${response.data.stores_added} stores with ${response.data.ads_generated} product ads!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scrape auto parts');
    } finally {
      setScrapingParts(false);
    }
  };

  const handleImprovePhotos = async () => {
    setImprovingPhotos(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      await axios.post(
        `${API}/scraper/improve-photos`,
        {},
        { headers, withCredentials: true }
      );
      
      toast.success('Photo improvement task queued!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start photo improvement');
    } finally {
      setImprovingPhotos(false);
    }
  };

  const handleSendTestNotification = async (type) => {
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      await axios.post(
        `${API}/notifications/test`,
        { type },
        { headers, withCredentials: true }
      );
      
      toast.success(`Test ${(type || 'notification').replace('_', ' ')} notification sent!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <Tabs defaultValue="scraper" className="space-y-4">
      <TabsList className="grid grid-cols-3 w-full max-w-lg">
        <TabsTrigger value="scraper" className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Scraper
        </TabsTrigger>
        <TabsTrigger value="api-keys" className="flex items-center gap-2">
          <Key className="h-4 w-4" />
          API Keys
        </TabsTrigger>
        <TabsTrigger value="notifications" className="flex items-center gap-2">
          <Bell className="h-4 w-4" />
          Notifications
        </TabsTrigger>
      </TabsList>

      {/* Scraper Tab */}
      <TabsContent value="scraper" className="space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-primary">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Utensils className="h-8 w-8 text-orange-600" />
                <div>
                  <p className="text-sm text-gray-600">Restaurants</p>
                  <p className="text-2xl font-bold">{stats.restaurants}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-success">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Utensils className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-sm text-gray-600">Menu Items</p>
                  <p className="text-2xl font-bold">{stats.menu_items}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Store className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Auto Parts Stores</p>
                  <p className="text-2xl font-bold">{stats.auto_parts_stores}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Wrench className="h-8 w-8 text-purple-600" />
                <div>
                  <p className="text-sm text-gray-600">Auto Parts Ads</p>
                  <p className="text-2xl font-bold">{stats.auto_parts_ads}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scrape Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Utensils className="h-5 w-5 text-orange-600" />
                Fort Wayne Restaurants
              </CardTitle>
              <CardDescription>
                Scrape all restaurants with online presence in Fort Wayne, IN
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="h-4 w-4" />
                <span>McDonald's, Burger King, Wendy's, Taco Bell, Chick-fil-A, Chipotle, Panera, and more...</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <DollarSign className="h-4 w-4" />
                <span>$8 minimum markup + 7% tax on all items</span>
              </div>
              <Button 
                onClick={handleScrapeAllRestaurants}
                disabled={scraping}
                className="w-full bg-orange-600 hover:bg-orange-700 gap-2"
              >
                {scraping ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Scraping Restaurants...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Scrape All Restaurants
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-blue-600" />
                Fort Wayne Auto Parts
              </CardTitle>
              <CardDescription>
                Scrape all auto parts stores and generate 1000+ product listings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Store className="h-4 w-4" />
                <span>AutoZone, O'Reilly, NAPA, Advance Auto Parts, Pep Boys</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Shield className="h-4 w-4" />
                <span>Admin approval required before customer charge</span>
              </div>
              <Button 
                onClick={handleScrapeAllAutoParts}
                disabled={scrapingParts}
                className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
              >
                {scrapingParts ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Scraping Auto Parts...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Scrape All Auto Parts
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Photo Improvement */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-purple-600" />
              Photo & Data Improvement
            </CardTitle>
            <CardDescription>
              Continuously improve photo matching, price accuracy, and location data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleImprovePhotos}
              disabled={improvingPhotos}
              variant="outline"
              className="gap-2"
            >
              {improvingPhotos ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Image className="h-4 w-4" />
                  Improve Photos & Data
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Recent Scrapes */}
        {stats.recent_scrapes?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Scrape Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.recent_scrapes.map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      {log.type === 'restaurants' ? (
                        <Utensils className="h-4 w-4 text-orange-600" />
                      ) : (
                        <Wrench className="h-4 w-4 text-blue-600" />
                      )}
                      <span className="capitalize">{log.type}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant="outline">
                        {log.restaurants_added || log.stores_added || 0} locations
                      </Badge>
                      <Badge variant="outline">
                        {log.menu_items_added || log.ads_generated || 0} items
                      </Badge>
                      <span className="text-gray-500">{formatDate(log.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* API Keys Tab */}
      <TabsContent value="api-keys" className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Keys
              </CardTitle>
              <CardDescription>
                Generate API keys for restaurant partners and authorized scraping
              </CardDescription>
            </div>
            <Button onClick={() => setShowNewKeyDialog(true)} className="gap-2">
              <Key className="h-4 w-4" />
              Generate New Key
            </Button>
          </CardHeader>
          <CardContent>
            {apiKeys.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Key className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No API keys generated yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <div key={key.key_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Key className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{key.name}</p>
                        <p className="text-sm text-gray-500 font-mono">{key.key_prefix}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant={key.is_active ? "default" : "secondary"}>
                        {key.is_active ? "Active" : "Revoked"}
                      </Badge>
                      <Badge variant="outline" className="capitalize">
                        {key.type}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        Used {key.usage_count || 0}x
                      </span>
                      {key.is_active && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRevokeKey(key.key_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generate Key Dialog */}
        <Dialog open={showNewKeyDialog} onOpenChange={setShowNewKeyDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate API Key</DialogTitle>
            </DialogHeader>
            
            {generatedKey ? (
              <div className="space-y-4 py-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">Key Generated!</span>
                  </div>
                  <p className="text-sm text-green-700 mb-4">
                    Save this key now - it won't be shown again!
                  </p>
                  <div className="bg-white rounded border p-3 font-mono text-sm break-all">
                    {generatedKey.api_key}
                  </div>
                  <Button 
                    onClick={() => copyToClipboard(generatedKey.api_key)}
                    variant="outline"
                    size="sm"
                    className="mt-2 gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy Key
                  </Button>
                </div>
                <DialogFooter>
                  <Button onClick={() => { setShowNewKeyDialog(false); setGeneratedKey(null); setNewKeyName(''); }}>
                    Done
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Key Name</label>
                  <Input 
                    placeholder="e.g., McDonald's API Access"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Key Type</label>
                  <Select value={newKeyType} onValueChange={setNewKeyType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="restaurant">Restaurant Partner</SelectItem>
                      <SelectItem value="admin">Admin Access</SelectItem>
                      <SelectItem value="partner">Business Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setShowNewKeyDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleGenerateKey} disabled={generatingKey} className="gap-2">
                    {generatingKey ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Key className="h-4 w-4" />
                        Generate Key
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </TabsContent>

      {/* Notifications Tab */}
      <TabsContent value="notifications" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Customer Notifications
            </CardTitle>
            <CardDescription>
              Email & SMS notifications for order updates (MOCKED for testing)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button 
                variant="outline" 
                onClick={() => handleSendTestNotification('order_approved')}
                className="flex-col h-auto py-4"
              >
                <Check className="h-5 w-5 mb-2 text-green-600" />
                <span className="text-xs">Order Approved</span>
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleSendTestNotification('driver_assigned')}
                className="flex-col h-auto py-4"
              >
                <Activity className="h-5 w-5 mb-2 text-blue-600" />
                <span className="text-xs">Driver Assigned</span>
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleSendTestNotification('order_delivered')}
                className="flex-col h-auto py-4"
              >
                <Check className="h-5 w-5 mb-2 text-purple-600" />
                <span className="text-xs">Order Delivered</span>
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleSendTestNotification('order_rejected')}
                className="flex-col h-auto py-4"
              >
                <Trash2 className="h-5 w-5 mb-2 text-red-600" />
                <span className="text-xs">Order Rejected</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notification Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notificationLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No notifications sent yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {notificationLogs.map((log) => (
                  <div key={log.log_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {log.type === 'email' ? (
                        <Mail className="h-4 w-4 text-blue-600" />
                      ) : (
                        <MessageSquare className="h-4 w-4 text-green-600" />
                      )}
                      <div>
                        <p className="text-sm font-medium capitalize">{(log.template || 'notification').replace('_', ' ')}</p>
                        <p className="text-xs text-gray-500">{log.to || ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={log.status === 'sent' ? 'default' : 'secondary'}>
                        {log.status || 'unknown'}
                      </Badge>
                      <span className="text-xs text-gray-500">{log.sent_at ? formatDate(log.sent_at) : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
